// ===========================================
// BUSTED! BLACKJACK - GAME STATE TYPES
// ===========================================
// State structures for the event-driven blackjack game
// These types represent the current state of a round/game
// ===========================================
import { GamePhase } from './enums.js';
import { PlayerGameStatus } from './game-events.js';
// ===========================================
// FACTORY FUNCTIONS
// ===========================================
export function createEmptyHandState(index, bet, betChips) {
    return {
        index,
        cards: [],
        bet,
        betChips,
        score: 0,
        isSoft: false,
        isDoubled: false,
        isSplit: false,
        isStanding: false,
        isBusted: false,
        isBlackjack: false,
        isSurrendered: false,
    };
}
export function createEmptyDealerState() {
    return {
        cards: [],
        score: 0,
        isSoft: false,
        holeCardRevealed: false,
        isBlackjack: false,
        isBusted: false,
        isStanding: false,
        visibleScore: 0,
        visibleCards: [],
    };
}
export function createPlayerGameState(oduserId, seatPosition, username, avatarUrl, balance) {
    return {
        oduserId,
        seatPosition,
        status: PlayerGameStatus.EMPTY,
        stagedBet: 0,
        confirmedBet: 0,
        hands: [],
        currentHandIndex: 0,
        totalBetAmount: 0,
        totalNetResult: 0,
        username,
        avatarUrl,
        balance,
        isSittingOut: false,
    };
}
export function createRoundState(tableId, roundNumber, buttonPosition) {
    return {
        id: `${tableId}-${roundNumber}-${Date.now()}`,
        tableId,
        roundNumber,
        phase: GamePhase.BETTING,
        buttonPosition,
        startedAt: Date.now(),
        playerGames: new Map(),
        dealer: createEmptyDealerState(),
        turnOrder: [],
        insuranceOffered: false,
        insuranceResponses: new Map(),
        events: [],
        lastSequenceNumber: 0,
    };
}
// ===========================================
// SCORE CALCULATION
// ===========================================
export function calculateHandScore(cards) {
    let score = 0;
    let aceCount = 0;
    for (const card of cards) {
        if (!card.faceUp)
            continue; // Don't count face-down cards
        if (card.rank === 'A') {
            aceCount++;
            score += 11;
        }
        else if (['K', 'Q', 'J'].includes(card.rank)) {
            score += 10;
        }
        else {
            score += parseInt(card.rank, 10);
        }
    }
    // Adjust for aces
    while (score > 21 && aceCount > 0) {
        score -= 10;
        aceCount--;
    }
    const isSoft = aceCount > 0 && score <= 21;
    // Calculate soft score (if applicable)
    let softScore;
    if (isSoft && score <= 11) {
        softScore = score + 10; // Could also be counted as higher
    }
    return { score, softScore, isSoft };
}
export function isBlackjack(cards) {
    if (cards.length !== 2)
        return false;
    const visibleCards = cards.filter(c => c.faceUp);
    if (visibleCards.length !== 2)
        return false;
    const { score } = calculateHandScore(visibleCards);
    return score === 21;
}
export function canSplit(hand, playerBalance) {
    if (hand.cards.length !== 2)
        return false;
    if (hand.isSplit)
        return false; // Already split (could allow re-splits later)
    if (playerBalance < hand.bet)
        return false;
    const card1 = hand.cards[0];
    const card2 = hand.cards[1];
    if (!card1 || !card2)
        return false;
    const card1Value = getCardValue(card1);
    const card2Value = getCardValue(card2);
    return card1Value === card2Value;
}
export function canDouble(hand, playerBalance) {
    if (hand.cards.length !== 2)
        return false;
    if (hand.isDoubled)
        return false;
    if (playerBalance < hand.bet)
        return false;
    return true;
}
export function canSurrender(hand) {
    // Can only surrender on initial two cards, no splits
    return hand.cards.length === 2 && !hand.isSplit;
}
function getCardValue(card) {
    if (['K', 'Q', 'J', '10'].includes(card.rank))
        return 10;
    if (card.rank === 'A')
        return 11;
    return parseInt(card.rank, 10);
}
// ===========================================
// TURN ORDER CALCULATION
// ===========================================
export function calculateTurnOrder(buttonPosition, playersWithBets) {
    // Turn order is clockwise from button (left of button acts first)
    // Seats are numbered 1-7
    const order = [];
    // Start from seat after button, going clockwise
    let seat = buttonPosition;
    for (let i = 0; i < 7; i++) {
        seat = (seat % 7) + 1; // Next seat clockwise (1->2->3...->7->1)
        if (playersWithBets.includes(seat)) {
            order.push(seat);
        }
    }
    return order;
}
export function calculateDealOrder(buttonPosition, playersWithBets) {
    // Deal order is same as turn order - clockwise from button
    return calculateTurnOrder(buttonPosition, playersWithBets);
}
//# sourceMappingURL=game-state.js.map